import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalShellComponent } from '../modal-shell/modal-shell.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantUsersService } from '../../services/tenant-users.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantUser } from '../../models/tenant-user.model';
import { ApiErrorResponse } from '../../models/auth.model';

export type TenantUserModalMode = 'add' | 'edit' | 'delete' | 'changePassword';

@Component({
  selector: 'app-tenant-users-modal',
  imports: [ModalShellComponent, ReactiveFormsModule, LoaderComponent],
  templateUrl: './tenant-users.component.html',
  styleUrl: './tenant-users.component.scss'
})
export class TenantUsersModalComponent implements OnInit {
  @Input() mode: TenantUserModalMode = 'add';
  @Input() tenantUid = '';
  @Input() user: TenantUser | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  form!: FormGroup<{
    username: FormControl<string | null>;
    email: FormControl<string | null>;
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    password: FormControl<string | null>;
  }>;

  loading = false;

  constructor(private fb: FormBuilder, private tenantUsersService: TenantUsersService, private toastrService: ToastrService) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      username: [this.identifier, this.mode === 'add' ? [Validators.required] : []],
      email: [this.user?.email ?? '', this.mode === 'add' || this.mode === 'edit' ? [Validators.required, Validators.email] : []],
      firstName: [this.user?.first_name ?? ''],
      lastName: [this.user?.last_name ?? ''],
      password: ['', this.mode === 'add' || this.mode === 'changePassword' ? [Validators.required, Validators.minLength(6)] : []]
    });

    if (this.mode === 'edit') {
      this.form.controls.username.disable();
    }
  }

  // The users-with-services list doesn't return `username` (only names/email/id), but the
  // create/update/delete endpoints are routed by username. Email is used as a best-effort
  // stand-in identifier until the backend exposes the real username on this endpoint.
  get identifier(): string {
    return this.user?.email ?? '';
  }

  get title(): string {
    switch (this.mode) {
      case 'add': return 'Add User';
      case 'edit': return 'Edit User';
      case 'changePassword': return 'Change Password';
      default: return 'Remove User';
    }
  }

  submit(): void {
    if (this.mode === 'delete') {
      this.runDelete();
      return;
    }

    if (this.mode === 'changePassword') {
      if (this.form.controls.password.invalid) {
        this.form.controls.password.markAsTouched();
        return;
      }
      this.runChangePassword();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.mode === 'add') {
      this.runCreate();
    } else {
      this.runUpdate();
    }
  }

  close(): void {
    if (this.loading) {
      return;
    }
    this.closed.emit();
  }

  private runCreate(): void {
    this.loading = true;

    const { username, email, firstName, lastName, password } = this.form.getRawValue();

    this.tenantUsersService.createUser({
      username: username!,
      email: email!,
      password: password!,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      tenant_uid: this.tenantUid || undefined
    }).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private runUpdate(): void {
    if (!this.identifier) {
      return;
    }
    this.loading = true;

    const { email, firstName, lastName } = this.form.getRawValue();

    this.tenantUsersService.updateUser(this.identifier, {
      email: email || undefined,
      first_name: firstName || undefined,
      last_name: lastName || undefined
    }).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private runChangePassword(): void {
    if (!this.identifier) {
      return;
    }
    this.loading = true;

    const { password } = this.form.getRawValue();

    this.tenantUsersService.updateUser(this.identifier, { password: password! }).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private runDelete(): void {
    if (!this.identifier) {
      return;
    }
    this.loading = true;

    this.tenantUsersService.deleteUser(this.identifier).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private finish(): void {
    this.loading = false;
    this.saved.emit();
    this.closed.emit();
  }

  private fail(err: HttpErrorResponse): void {
    this.loading = false;
    const body = err.error as ApiErrorResponse | undefined;
    this.toastrService.error(body?.detail ?? body?.error?.message ?? 'Something went wrong. Please try again.');
  }
}
